// src/brokerageadmin/QuoteyDashboard/ReportModal.jsx
import React, { useState, useMemo, useEffect } from 'react';
import './QuoteyDashboard.css';

const ReportModal = ({ show, onClose, title, data, isLoading, error, reportType, onItemClick }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Reset search term when modal is closed or data changes
    useEffect(() => {
        if (!show) {
            setSearchTerm('');
        }
    }, [show]);

    // Filter data based on search term (must be before early return)
    const filteredData = useMemo(() => {
        if (!data || !searchTerm) return data;

        return data.filter(item => {
            const name = item.name || item.code || item.brokerId || '';
            return name.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [data, searchTerm]);

    if (!show) return null;

    let headers = [];
    if (reportType === 'brokerage' || reportType === 'vertical') {
        headers = [reportType === 'brokerage' ? 'Brokerage' : 'Vertical', 'Prospects', '% of Total', 'Active', 'Binded'];
    } else { // Policy reports (insurer, broker)
        headers = [reportType === 'broker' ? 'Broker' : (reportType === 'vertical' ? 'Vertical' : 'Insurer'), 'Policies', '% of Total'];
    }

    const handleRowClick = (item) => {
        // Only trigger onItemClick if reportType is 'insurer' and onItemClick prop is provided
        if (reportType === 'insurer' && onItemClick) {
            onClose(); // Close the current ReportModal first
            onItemClick(item.name); // 'name' holds the insurer name from the report data
        }
    };

    return (
        <div className="report-modal-overlay" onClick={onClose}>
            <div className="report-modal-content" onClick={e => e.stopPropagation()}>
                <button className="report-modal-close" onClick={onClose}>×</button>
                <h2>{title}</h2>

                {/* Search input */}
                {!isLoading && !error && data && data.length > 0 && (
                    <div style={{ marginBottom: '15px' }}>
                        <input
                            type="text"
                            placeholder={`Search by ${headers[0].toLowerCase()}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                boxSizing: 'border-box'
                            }}
                        />
                        {searchTerm && (
                            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                                Showing {filteredData?.length || 0} of {data.length} results
                            </div>
                        )}
                    </div>
                )}

                {isLoading ? <div className="loading-cell"><div className="loading-spinner"/>Loading...</div>
                : error ? <div className="error-message">Error: {error}</div>
                : !data || data.length === 0 ? <p>No data available.</p>
                : filteredData && filteredData.length === 0 ? (
                    <p>No results found for "{searchTerm}"</p>
                ) : (<table className="data-table" style={{ marginTop: '10px' }}>
                    <thead><tr>{headers.map(h => <th key={h} className={['% of Total', 'Policies', 'Prospects', 'Active', 'Binded'].includes(h) ? "text-right" : "text-left"}>{h}</th>)}</tr></thead>
                    <tbody>{filteredData.map((item, index) => (
                        <tr
                            key={`${item.name || item.code || item._id}-${index}`}
                            // Apply styling and click handler only for 'insurer' type reports
                            className={reportType === 'insurer' ? 'clickable-row' : ''}
                            onClick={reportType === 'insurer' ? () => handleRowClick(item) : undefined}
                            style={{ cursor: reportType === 'insurer' ? 'pointer' : 'default' }}
                        >
                             <td className="text-left">{item.name || item.code || item.brokerId || 'Unknown'}</td>
                             <td className="text-right">{item.total ?? item.count ?? 0}</td> {/* Use item.total for prospect, item.count for policy */}
                             <td className="text-right">{item.percentage}%</td>
                             {(reportType === 'brokerage' || reportType === 'vertical') && (
                                 <>
                                     <td className="text-right">{item.active ?? 0}</td>
                                     <td className="text-right">{item.binded ?? 0}</td>
                                 </>
                             )}
                        </tr>))}
                    </tbody></table>)}
            </div>
        </div>
    );
};

export default ReportModal;